'use client';

import { cn } from '@zealot/ui/lib/utils';
import type { LucideIcon } from 'lucide-react';
import { Loader2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  type AlertDialogPopupProps,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogViewport,
} from './alert-dialog';
import { Button } from './button';

type ConfirmationDialogVariant = 'default' | 'destructive' | 'secondary' | 'warning';

// gloss
type ConfirmationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: React.ReactNode;
  children?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void | Promise<void>;
  isLoading?: boolean | string;
  variant?: ConfirmationDialogVariant;
  icon?: LucideIcon;
  className?: string;
  size?: AlertDialogPopupProps['size'];
};

const variantStyles: Record<
  ConfirmationDialogVariant,
  { iconContainer: string; confirmVariant: 'default' | 'destructive' | 'secondary'; loadingText: string }
> = {
  default: {
    iconContainer: 'bg-primary/10 text-primary',
    confirmVariant: 'default',
    loadingText: 'Processing...',
  },
  destructive: {
    iconContainer: 'bg-destructive/10 text-destructive',
    confirmVariant: 'destructive',
    loadingText: 'Removing...',
  },
  secondary: {
    iconContainer: 'bg-secondary text-secondary-foreground',
    confirmVariant: 'secondary',
    loadingText: 'Processing...',
  },
  warning: {
    iconContainer: 'bg-warning/10 text-warning-foreground',
    confirmVariant: 'default',
    loadingText: 'Processing...',
  },
};

const ConfirmationDialog = ({
  open,
  onOpenChange,
  title,
  description,
  children,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  isLoading = false,
  variant = 'default',
  icon: Icon,
  className,
  size,
}: ConfirmationDialogProps) => {
  const styles = variantStyles[variant];
  const isLoadingActive = Boolean(isLoading);
  const loadingText = typeof isLoading === 'string' ? isLoading : styles.loadingText;

  return (
    <AlertDialog open={open} onOpenChange={(nextOpen) => !isLoadingActive && onOpenChange(nextOpen)}>
      <AlertDialogPortal>
        <AlertDialogBackdrop />
        <AlertDialogViewport>
          <AlertDialogPopup className={className} size={size}>
            <AlertDialogHeader>
              <div className="flex items-center gap-3">
                {Icon && (
                  <div
                    className={cn('flex size-9 shrink-0 items-center justify-center rounded-lg', styles.iconContainer)}
                  >
                    <Icon className="size-4" />
                  </div>
                )}
                <AlertDialogTitle>{title}</AlertDialogTitle>
              </div>
              <AlertDialogDescription className={cn(Icon && 'pl-12')}>{description}</AlertDialogDescription>
            </AlertDialogHeader>
            {children}
            <AlertDialogFooter>
              <AlertDialogClose
                render={(props) => (
                  <Button {...props} variant="outline" disabled={isLoadingActive}>
                    {cancelText}
                  </Button>
                )}
              />
              <Button variant={styles.confirmVariant} disabled={isLoadingActive} onClick={onConfirm}>
                {isLoadingActive ? (
                  <>
                    <Loader2 className="animate-spin" />
                    {loadingText}
                  </>
                ) : (
                  confirmText
                )}
              </Button>
            </AlertDialogFooter>
          </AlertDialogPopup>
        </AlertDialogViewport>
      </AlertDialogPortal>
    </AlertDialog>
  );
};

export type { ConfirmationDialogProps, ConfirmationDialogVariant };
export { ConfirmationDialog };
